'use client';

import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["lines", "className", "classes", "slots", "slotProps", "sx"];
import * as React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { keyframes } from '@mui/system';
import useSlotProps from '@mui/utils/useSlotProps';
import { styled, createUseThemeProps } from "../internals/zero-styled/index.mjs";
import { useChatMessageSkeletonUtilityClasses } from "./chatMessageSkeletonClasses.mjs";
import { jsx as _jsx } from "react/jsx-runtime";
const useThemeProps = createUseThemeProps('MuiChatMessageSkeleton');
const shimmerKeyframes = keyframes`
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;
const ChatMessageSkeletonRootStyled = styled('div', {
  name: 'MuiChatMessageSkeleton',
  slot: 'Root',
  overridesResolver: (_, styles) => styles.root
})(({
  theme
}) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  padding: theme.spacing(0.5, 0)
}));
const ChatMessageSkeletonLineStyled = styled('div', {
  name: 'MuiChatMessageSkeleton',
  slot: 'Line',
  overridesResolver: (_, styles) => styles.line
})(({
  theme
}) => ({
  height: '0.875em',
  borderRadius: theme.shape.borderRadius,
  background: `linear-gradient(
    90deg,
    ${(theme.vars || theme).palette.action.hover} 25%,
    ${(theme.vars || theme).palette.action.selected} 37%,
    ${(theme.vars || theme).palette.action.hover} 63%
  )`,
  backgroundSize: '400% 100%',
  animation: `${shimmerKeyframes} 1.4s ease infinite`,
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none'
  },
  '&:last-child': {
    width: '60%'
  }
}));
const ChatMessageSkeleton = /*#__PURE__*/React.forwardRef(function ChatMessageSkeleton(inProps, ref) {
  const props = useThemeProps({
    props: inProps,
    name: 'MuiChatMessageSkeleton'
  });
  const {
      lines = 3,
      className,
      classes: classesProp,
      slots,
      slotProps,
      sx
    } = props,
    other = _objectWithoutPropertiesLoose(props, _excluded);
  const classes = useChatMessageSkeletonUtilityClasses(classesProp);
  const Root = slots?.root ?? ChatMessageSkeletonRootStyled;
  const Line = slots?.line ?? ChatMessageSkeletonLineStyled;
  const rootProps = useSlotProps({
    elementType: Root,
    externalSlotProps: slotProps?.root,
    externalForwardedProps: other,
    ownerState: {},
    additionalProps: {
      ref,
      className: clsx(classes.root, className),
      sx
    }
  });
  const lineSlotProps = slotProps?.line;
  return /*#__PURE__*/_jsx(Root, _extends({}, rootProps, {
    children: Array.from({
      length: lines
    }, (_, i) => /*#__PURE__*/_jsx(Line, _extends({}, lineSlotProps, {
      className: clsx(classes.line, lineSlotProps?.className)
    }), i))
  }));
});
if (process.env.NODE_ENV !== "production") ChatMessageSkeleton.displayName = "ChatMessageSkeleton";
process.env.NODE_ENV !== "production" ? ChatMessageSkeleton.propTypes = {
  // ----------------------------- Warning --------------------------------
  // | These PropTypes are generated from the TypeScript type definitions |
  // | To update them edit the TypeScript types and run "pnpm proptypes"  |
  // ----------------------------------------------------------------------
  classes: PropTypes.object,
  className: PropTypes.string,
  /**
   * Number of shimmer lines to render.
   * @default 3
   */
  lines: PropTypes.number,
  slotProps: PropTypes.object,
  slots: PropTypes.object,
  sx: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.bool])), PropTypes.func, PropTypes.object])
} : void 0;
export { ChatMessageSkeleton };